import { forwardRef } from "react";
import { ExternalLink } from "lucide-react";
import clsx from "clsx";

export type LinkVariant = "default" | "subtle" | "inverse";
export type LinkSize = "sm" | "md" | "lg";

export type LinkProps = {
  variant?: LinkVariant;
  size?: LinkSize;
  external?: boolean;
  underline?: "always" | "hover" | "none";
} & React.AnchorHTMLAttributes<HTMLAnchorElement>;

const variantStyles: Record<LinkVariant, string> = {
  default: "text-[var(--color-accent)] hover:text-[var(--color-accent-hover)]",
  subtle:  "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]",
  inverse: "text-[var(--color-text-inverse)] hover:opacity-80",
};

const sizeStyles: Record<LinkSize, string> = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-base",
};

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { variant = "default", size = "md", external, underline = "hover", className, children, target, rel, ...rest },
  ref,
) {
  const computedTarget = target ?? (external ? "_blank" : undefined);
  const computedRel = rel ?? (external ? "noopener noreferrer" : undefined);
  return (
    <a
      ref={ref}
      target={computedTarget}
      rel={computedRel}
      className={clsx(
        "inline-flex items-center gap-1 transition-colors",
        variantStyles[variant],
        sizeStyles[size],
        underline === "always" && "underline",
        underline === "hover" && "hover:underline",
        underline === "none" && "no-underline",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-1 rounded-sm",
        className,
      )}
      {...rest}
    >
      {children}
      {external && <ExternalLink size={12} aria-hidden />}
    </a>
  );
});
